import debounce from 'lodash/debounce';
import {useEffect, useRef} from 'react';
import {PASSIVE_TOUCH_IF_POSSIBLE} from '../../common/constant';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {THRESHOLD_TO_MOVE_EPISODE} from '../common';
import {IViewerState} from '../type';

interface IProps {
  getIsStart: () => boolean;
  getIsEnd: () => boolean;
  goToPrevEpisode: () => void;
  goToNextEpisode: () => void;
}

export default function useMoveEpisode({
  getIsStart,
  getIsEnd,
  goToPrevEpisode,
  goToNextEpisode,
}: IProps) {
  const [viewerState, isRunMode] = useTypedSelector(({viewer}) => [
    viewer.viewerState,
    viewer.isRunMode,
  ]);

  const startPosRef = useRef<number>();
  const endDeltaYRef = useRef<number>();
  const goToNextEpisodeOnce = debounce(
    () => {
      // 정주행 모두 뷰어 끝에서 여러번 스크롤했을때 최초에만 실행, 멈췄다가 0.5초 후 재시도시 반응
      goToNextEpisode();
    },
    500,
    {leading: true, trailing: false}
  );
  useEffect(() => {
    if (FULL_COVER_VIEWER_STATE.includes(viewerState) || !isRunMode) return;

    function onTouchStart(e: TouchEvent | MouseEvent) {
      const touch = (getIsStart() || getIsEnd()) && getPointer(e);
      if (touch) {
        startPosRef.current = touch.clientY;
      } else {
        startPosRef.current = undefined;
      }
    }

    function onTouchEnd(e: TouchEvent | MouseEvent) {
      const touch = getPointer(e, true);
      const startPos = startPosRef.current;
      if (touch && startPos !== undefined) {
        const dist = touch.clientY - startPos;
        if (getIsStart() && dist > THRESHOLD_TO_MOVE_EPISODE) {
          goToPrevEpisode();
        } else if (getIsEnd() && dist < -THRESHOLD_TO_MOVE_EPISODE) {
          goToNextEpisodeOnce();
        }
      }
    }

    function onWheel(e: WheelEvent) {
      // 휠은 시작과 끝을 정확히 판단하기 어렵기 때문에 끝에서 아래 방향의 delta값만 확인한다.(다음웹툰 참고)
      // 스크롤과 이어져서 쉽게 발생할 수 있기 때문에 delta값을 적당히 키워서 판단한다.
      if (!getIsStart() && !getIsEnd()) {
        endDeltaYRef.current = undefined;
        return;
      }
      const endDeltaY = endDeltaYRef.current;
      if (
        getIsStart() &&
        endDeltaY &&
        e.deltaY - endDeltaY < -THRESHOLD_TO_MOVE_EPISODE
      ) {
        window.removeEventListener('wheel', onWheel, PASSIVE_TOUCH_IF_POSSIBLE);
        endDeltaYRef.current = undefined;
        goToPrevEpisode();
      } else if (
        getIsEnd() &&
        endDeltaY &&
        e.deltaY - endDeltaY > THRESHOLD_TO_MOVE_EPISODE
      ) {
        window.removeEventListener('wheel', onWheel, PASSIVE_TOUCH_IF_POSSIBLE);
        endDeltaYRef.current = undefined;
        goToNextEpisodeOnce();
        return;
      } else {
        endDeltaYRef.current = e.deltaY;
      }
    }

    window.addEventListener(
      'touchstart',
      onTouchStart,
      PASSIVE_TOUCH_IF_POSSIBLE
    );
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    window.addEventListener(
      'mousedown',
      onTouchStart,
      PASSIVE_TOUCH_IF_POSSIBLE
    );
    window.addEventListener('mouseleave', onTouchEnd);
    window.addEventListener('mouseup', onTouchEnd);

    window.addEventListener('wheel', onWheel, PASSIVE_TOUCH_IF_POSSIBLE);

    return () => {
      window.removeEventListener(
        'touchstart',
        onTouchStart,
        PASSIVE_TOUCH_IF_POSSIBLE
      );
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener(
        'mousedown',
        onTouchStart,
        PASSIVE_TOUCH_IF_POSSIBLE
      );
      window.removeEventListener('mouseleave', onTouchEnd);
      window.removeEventListener('mouseup', onTouchEnd);
      window.removeEventListener('wheel', onWheel, PASSIVE_TOUCH_IF_POSSIBLE);
    };
  });
}

const FULL_COVER_VIEWER_STATE: IViewerState[] = ['comment', 'setting'];

function getPointer(e: TouchEvent | MouseEvent, isUseChanged = false) {
  if ('touches' in e) {
    const touches = isUseChanged ? e.changedTouches : e.touches;
    return touches.length === 1 ? touches[0] : undefined;
  } else {
    return e;
  }
}
